import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './styles.css'

import type { TravelLog } from './types'
import { CREATE_ITEM_MIN_ZOOM, isEmbedMode, state } from './state'
import { isPlainObject } from './utils'
import { els } from './dom'
import {
  blobToDataUrl,
  dataUrlToBlob,
  deleteMedia,
  getMedia,
  // loadItems,
  migrateItem,
  saveItems,
  saveMedia,
  loadTravelLogs
} from './storage'
import { revokeCachedMedia } from './media'
import { loadLang, setLang, t } from './i18n'
import { applyPreferences, loadTheme, setTheme } from './theme'
import {
  findCountryAtLatLng,
  isLatLngInsideSelectedCountry,
  loadCountries,
} from './countries'
import {
  attachResizeObserver,
  centerCountry,
  centerMapOnLatLng,
  map,
  renderCountryLayer,
  renderMarkers,
} from './map'
import {
  applyTranslations,
  renderCountryDetails,
  renderStats,
  toggleDrawer,
} from './ui'
import { closeCreateModal, renderCreatePopup, renderEditPopup } from './modal'
import { handleEmbedMessage } from './embed'

// ---- Bootstrap ---------------------------------------------------------------

state.items = isEmbedMode ? [] : loadTravelLogs()
state.theme = loadTheme()

attachResizeObserver()
document.body.classList.toggle('embed', isEmbedMode)
applyPreferences()
applyTranslations()
void init()

async function init(): Promise<void> {
  bindEvents()
  render()
  try {
    await loadCountries()
    els.mapStatus.textContent = ''
    render()
  } catch {
    els.mapStatus.textContent = t('couldNotLoadCountries')
  }
  map.invalidateSize()
}

// ---- Top-level orchestration -------------------------------------------------

function render(): void {
  applyTranslations()
  renderStats()
  renderCountryDetails(render)
  renderCountryLayer(handleMapClick)
  renderMarkers(
    onMarkerClick,
    isEmbedMode
      ? undefined
      : {
          onEdit: (item) => renderEditPopup(item, render),
          onDelete: (item) => void handleDeleteItem(item),
        },
  )
}

function onMarkerClick(item: TravelLog): void {
  state.selectedCountry = item.country
  renderCountryDetails(render)
}

async function handleDeleteItem(item: TravelLog): Promise<void> {
  state.items = state.items.filter((current) => current.id !== item.id)
  const blobsToDelete = item.media
    .filter((m) => m.source === 'indexeddb' && m.blobId)
    .map((m) => m.blobId)
  await Promise.all(blobsToDelete.map(deleteMedia))
  revokeCachedMedia(blobsToDelete)
  saveItems(state.items)
  map.closePopup()
  render()
}

function handleMapClick(latlng: L.LatLng): void {
  if (isEmbedMode) return
  const country = findCountryAtLatLng(latlng) || ''
  const clickedSelected = isLatLngInsideSelectedCountry(latlng)

  if (!country) {
    if (state.selectedCountry && map.getZoom() >= CREATE_ITEM_MIN_ZOOM) {
      centerMapOnLatLng(latlng, map.getZoom(), () =>
        renderCreatePopup(latlng, state.selectedCountry, render),
      )
      return
    }
    closeCreateModal()
    map.closePopup()
    return
  }

  if (!clickedSelected) {
    state.selectedCountry = country
    closeCreateModal()
    map.closePopup()
    render()
    centerCountry(country)
    els.mapStatus.textContent = t('countrySelectedHint')
    return
  }

  if (map.getZoom() < CREATE_ITEM_MIN_ZOOM) {
    els.mapStatus.textContent = t('zoomHint')
    closeCreateModal()
    map.closePopup()
    return
  }

  centerMapOnLatLng(latlng, map.getZoom(), () =>
    renderCreatePopup(latlng, state.selectedCountry, render),
  )
}

function bindEvents(): void {
  map.on('click', (event: L.LeafletMouseEvent) => handleMapClick(event.latlng))
  L.DomEvent.disableClickPropagation(els.topbar)
  L.DomEvent.disableScrollPropagation(els.topbar)

  els.clearButton.addEventListener('click', () => void clearAllData())
  els.exportButton.addEventListener('click', () => void exportBackup())
  els.importInput.addEventListener('change', (event) => void importBackup(event))

  els.themeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const choice = button.dataset.themeChoice
      if (choice === 'light' || choice === 'dark') {
        setTheme(choice)
        applyPreferences()
        renderCountryLayer(handleMapClick)
        applyTranslations()
      }
    })
  })
  els.languageButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const choice = button.dataset.langChoice
      if (choice === 'en' || choice === 'pt') {
        setLang(choice)
        applyPreferences()
        render()
      }
    })
  })

  els.drawerToggle.addEventListener('click', toggleDrawer)
  window.addEventListener('message', (event) =>
    void handleEmbedMessage(event, render),
  )
}

// ---- Top-level user actions (clear / export / import) ----------------------

async function clearAllData(): Promise<void> {
  const confirmed = window.confirm(t('deleteAllConfirm'))
  if (!confirmed) return
  const blobIds = state.items.flatMap((item) =>
    item.media
      .filter((m) => m.source === 'indexeddb' && m.blobId)
      .map((m) => m.blobId),
  )
  await Promise.all(blobIds.map(deleteMedia))
  revokeCachedMedia(blobIds)
  state.items = []
  state.selectedCountry = ''
  saveItems(state.items)
  render()
}

async function exportBackup(): Promise<void> {
  const media: Record<string, string> = {}
  for (const item of state.items) {
    for (const m of item.media) {
      if (m.source !== 'indexeddb' || !m.blobId || media[m.blobId]) continue
      const blob = await getMedia(m.blobId)
      if (!blob) continue
      media[m.blobId] = await blobToDataUrl(blob)
    }
  }
  const backup = {
    version: 3,
    exportedAt: new Date().toISOString(),
    items: state.items,
    media,
  }
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = `been-web-backup-${new Date().toISOString().slice(0, 10)}.json`
  link.click()
  URL.revokeObjectURL(url)
}

async function importBackup(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  try {
    const backup = JSON.parse(await file.text()) as {
      items?: unknown
      media?: unknown
    }
    const rawItems = Array.isArray(backup.items)
      ? (backup.items as Array<Record<string, unknown>>)
      : []
    const media = isPlainObject(backup.media)
      ? (backup.media as Record<string, string>)
      : {}
    // Persist items first so the import survives even if some media blobs fail
    // (e.g. an oversized video that can't be decoded on this device).
    state.items = rawItems.map(migrateItem)
    state.selectedCountry = state.items[0]?.country ?? ''
    saveItems(state.items)
    render()

    let failed = 0
    for (const [blobId, dataUrl] of Object.entries(media)) {
      try {
        await saveMedia(blobId, dataUrlToBlob(dataUrl))
      } catch {
        failed += 1
      }
    }
    render()
    els.mapStatus.textContent = failed
      ? `${t('backupImported')} (${failed} media skipped)`
      : t('backupImported')
  } catch {
    els.mapStatus.textContent = t('backupFailed')
  } finally {
    input.value = ''
  }
}
